/**
 * /genre-mixer — AI Genre Mixer.
 *
 * Ten sections, in the prototype's order: genre selection with a compatibility ring,
 * creative controls, a DNA preview, the live prompt generator, AI variations, preview
 * chips, Create, the processing state, the result card, and "how it works". Layout, copy
 * and styles are otherwise verbatim from the monolith's `view === "genreMixer"` block.
 *
 * THE FLOW IS NOW STAGED, which is the one structural change to this screen.
 *
 *   preparing → genres → (Mix) → mixing → details → (Create) → generating → result
 *
 * It used to have no steps at all: the entire lower half of the page — controls, DNA
 * preview, prompt, variations, Create — appeared the instant a second dropdown changed,
 * with nothing to press in between. So choosing genres and filling in details were the
 * same moment, and the screen never acknowledged the choice. There is now a Mix button
 * that commits it, and the details are prepared in response.
 *
 * The two processing states, `preparing` and `mixing`, hold for PROCESSING_MS and DO
 * NOTHING ELSE. No genres are blended in either, no request is sent, and the store is
 * unchanged when they end — the details they reveal are derived from the settings and
 * were computable the whole time. They are honest about being a UX state and nothing
 * more; see lib/processing.ts, and note that the generation state below them is a real
 * wait for a real (simulated) API call.
 *
 * SIMULATED. No genres are blended anywhere. The song comes from generationApi's
 * fabricated generator, and the compatibility score and DNA preview are hashes of the
 * settings. See src/stores/mixerStore.ts.
 */
import { useEffect } from 'react';
import { BookOpen, ChevronRight, Music2, Shuffle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Btn, PreviewChip, SectionHeading } from '../components/ui';
import { downloadTextFile } from '../lib/download';
import { ProcessingWave } from '../components/studio';
import {
  MixerControls,
  MixerDnaPreview,
  MixerGenrePicker,
  MixerPromptPanel,
  MixerResultCard,
} from '../components/mixer';
import { GM_VARIATIONS } from '../data/music';
import { mapMixerGenreToAppGenre } from '../lib/music';
import { processingDelay } from '../lib/processing';
import { useSongs } from '../hooks';
import {
  MIXER_COST,
  MIXER_ERRORS,
  MIXER_MIN_GENRES,
  selectCompatibility,
  selectGeneratedPrompt,
  selectGenreCount,
  selectMixerDna,
  selectMixerReady,
  useMixerStore,
} from '../stores/mixerStore';
import { useCreateStore } from '../stores/createStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/** Delay before the auto-generated prompt is written, matching the prototype. */
const PROMPT_DEBOUNCE_MS = 400;

/**
 * The two processing states' words.
 *
 * Named up here, together, because the honesty of this screen depends on them being
 * read side by side: neither may claim work that is not happening. "Preparing" and
 * "Blending" describe the workspace being set up and the details being assembled, which
 * is all that occurs — no genres are mixed and no request is sent in either. The third,
 * the one during generation, is the only one describing a real request.
 */
const WAITING = {
  preparing: {
    title: 'Preparing your mixing workspace',
    detail: 'Loading the genre library and compatibility data.',
  },
  mixing: {
    title: 'Blending your genres',
    detail: 'Working out the compatibility, the music DNA and a prompt to match.',
  },
  generating: {
    title: 'Creating your song',
    detail: 'This usually takes about a minute or two. Please hold.',
  },
} as const;

/** The six steps in the "how it works" strip. */
const HOW_IT_WORKS = [
  'Choose Genres',
  'Analyze Compatibility',
  'Generate Music DNA',
  'Build AI Prompt',
  'Create Original Song',
  'Open in Studio',
];

export default function GenreMixer() {
  const navigate = useNavigate();

  // One field per selector. Zustand v5 compares with Object.is, so an object
  // literal would re-render this route on every unrelated store change.
  const genre1 = useMixerStore((s) => s.genre1);
  const genre2 = useMixerStore((s) => s.genre2);
  const genre3 = useMixerStore((s) => s.genre3);
  const mood = useMixerStore((s) => s.mood);
  const energy = useMixerStore((s) => s.energy);
  const tempo = useMixerStore((s) => s.tempo);
  const language = useMixerStore((s) => s.language);
  const vocal = useMixerStore((s) => s.vocal);
  const length = useMixerStore((s) => s.length);
  const variation = useMixerStore((s) => s.variation);
  const variant = useMixerStore((s) => s.variant);
  const prompt = useMixerStore((s) => s.prompt);
  const extraDescription = useMixerStore((s) => s.extraDescription);
  const promptDirty = useMixerStore((s) => s.promptDirty);
  const generating = useMixerStore((s) => s.generating);
  const stage = useMixerStore((s) => s.stage);
  const resultSongId = useMixerStore((s) => s.resultSongId);
  const error = useMixerStore((s) => s.error);
  const setSetting = useMixerStore((s) => s.setSetting);
  const setPrompt = useMixerStore((s) => s.setPrompt);
  const setExtraDescription = useMixerStore((s) => s.setExtraDescription);
  const selectVariation = useMixerStore((s) => s.selectVariation);
  const regeneratePrompt = useMixerStore((s) => s.regeneratePrompt);
  const applyGeneratedPrompt = useMixerStore((s) => s.applyGeneratedPrompt);
  const setError = useMixerStore((s) => s.setError);
  const setStage = useMixerStore((s) => s.setStage);
  const setGenerating = useMixerStore((s) => s.setGenerating);
  const setResultSongId = useMixerStore((s) => s.setResultSongId);

  const { getSong, createSong, downloadSong } = useSongs();
  const showToast = useUiStore((s) => s.showToast);
  // The same handover Music DNA uses to send a prompt to Create — see createStore.
  const loadIntoCreate = useCreateStore((s) => s.load);
  const setCreateResultSongId = useCreateStore((s) => s.setSurfaceResultSongId);

  const settings = {
    genre1,
    genre2,
    genre3,
    mood,
    energy,
    tempo,
    language,
    vocal,
    length,
    variation,
    variant,
  };
  const ready = selectMixerReady(settings);
  const genreCount = selectGenreCount(settings);
  const compatibility = selectCompatibility(settings);
  const dna = selectMixerDna(settings);
  const resultSong = getSong(resultSongId ?? undefined);
  const generatedPrompt = selectGeneratedPrompt(settings);

  /*
    The entry state: one processing floor of setting up, once, then the genre picker.

    Guarded on the stage rather than run on every mount, because this store outlives
    the route — walking to the Library and back mid-mix would otherwise replay the wait
    and, worse, throw away a set of details the user had already filled in. Only a
    reset() puts the screen back into `preparing`.

    The timer is cancelled on unmount via the `live` flag rather than a clearTimeout,
    because processingDelay hands back a promise and not a handle; the effect of a
    cancelled wait is that setStage is never called, which is what matters.
  */
  useEffect(() => {
    if (stage !== 'preparing') return;
    let live = true;
    void processingDelay().then(() => {
      if (live) setStage('genres');
    });
    return () => {
      live = false;
    };
  }, [stage, setStage]);

  // Auto-generates the prompt whenever a setting changes, unless the user has
  // hand-edited it. Debounced so dragging the energy slider does not rebuild the
  // prompt on every pixel.
  useEffect(() => {
    if (!ready || promptDirty) return;
    const t = setTimeout(() => applyGeneratedPrompt(generatedPrompt), PROMPT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [ready, promptDirty, generatedPrompt, applyGeneratedPrompt]);

  /**
   * Mix — commits the chosen genres and opens the details.
   *
   * The wait in the middle prepares nothing: the compatibility score, the DNA
   * preview and the prompt are all derived from settings that are already set, and the
   * store is identical either side of the wait. What the step actually buys is a moment
   * where the choice has been made and acknowledged, which the screen previously had no
   * way to express — see the stage comment in stores/mixerStore.ts.
   *
   * Guarded rather than merely disabled: the button is unreachable without two genres,
   * but a guard here is what makes that a rule of the flow instead of a property of one
   * button's props.
   */
  async function mix() {
    setError('');
    if (!ready) {
      setError(MIXER_ERRORS.noGenres);
      return;
    }
    setStage('mixing');
    await processingDelay();
    // Re-read rather than trusting the closure: a genre change during the wait sends
    // the stage back to 'genres' (setSetting does that), and this must not overrule it.
    if (useMixerStore.getState().stage === 'mixing') setStage('details');
  }

  async function generate() {
    setError('');
    if (!ready) {
      setError(MIXER_ERRORS.noGenres);
      return;
    }
    if (!prompt.trim()) {
      setError(MIXER_ERRORS.noPrompt);
      return;
    }
    if (!extraDescription.trim()) {
      setError(MIXER_ERRORS.noExtraDescription);
      return;
    }

    const submittedPrompt = [prompt.trim(), extraDescription.trim()].join('\n\n');

    setGenerating(true);
    setResultSongId(null);

    try {
      // createSong holds its own five-second processing floor (see hooks/useSongs), so
      // the ProcessingWave below stays up for at least that long without this screen
      // timing anything itself. The four ticking progress rows that used to sit here
      // were removed with it: they counted a 320ms interval unrelated to the request,
      // which is the definition of faked progress.
      /*
        The mixer's own language reaches the request too.

        It has always collected one — it is in MixerControls and it is written into the
        generated prompt — but the resulting song came back stamped English, because
        nothing forwarded the field. That is the same defect §4 names on Create, in a
        screen that already had the control, so it is fixed with the same field rather
        than left for the prompt text to imply.

        No vocal is passed: the mixer's vocal vocabulary is GM_VOCAL_TYPES ("Duet",
        "Choir"), not the composer's VOCALS, so its values would not match what
        generationApi tests for. Its "Instrumental" case is carried by the language
        instead, which is the field that records it on the song.
      */
      const song = await createSong(
        submittedPrompt,
        mapMixerGenreToAppGenre(genre1),
        MIXER_COST,
        undefined,
        language,
        // Names the path, because it is one of the two Lyrixa Composer+ sells: the funnel
        // refuses it without the plan, before charging anything. The route gate in
        // router.tsx means an unentitled visitor never reaches this button — this is what
        // holds if one does. See hooks/useSongs.METHOD_ENTITLEMENT.
        { method: 'mixer' }
      );
      // createSong returns null on a short balance, and now also when the account does
      // not own Composer+ — it raises the out-of-credits modal or the toast itself, so
      // there is nothing to report here beyond stopping.
      if (!song) return;
      setResultSongId(song.id);
      setCreateResultSongId('create', song.id);
      showToast('Genre mix generated — song is ready');
      /*
        Then hand the song to Create and go there, which is where the product asks for
        the result to be played. Music DNA's "one-click generate" already ends this way,
        so the two AI creation paths now finish in the same place rather than each having
        its own idea of where a finished song lives.

        loadIntoCreate seeds the composer with the prompt and genre that produced it, so
        Create opens describing this song rather than whatever was last typed there; its
        result panel shows the newest generation, which is this one, with a transport.

        The mix is not lost by leaving. resultSongId is in the store, so the result card
        in section 8 is still here on the way back — that is what the store is for.
      */
      // The language goes across as well now that Create has a picker for it, so the
      // composer opens describing this song in full rather than showing English beside
      // a prompt that says otherwise. 'Instrumental' is not one of Create's options —
      // its Vocals row expresses that — so it is left for the composer's own default.
      loadIntoCreate({
        prompt: submittedPrompt,
        genre: mapMixerGenreToAppGenre(genre1),
        language: language === 'Instrumental' ? undefined : language,
      });
      navigate(paths.create);
    } finally {
      // Cleared in `finally`: a rejected generation used to leave the button disabled
      // for the rest of the session.
      setGenerating(false);
    }
  }

  return (
    <div>
      <SectionHeading
        as="h1"
        icon={<Shuffle size={17} color={c.accent} />}
        title="AI Genre Mixer"
        subtitle="Blend multiple music genres together and create completely original songs powered by AI."
        marginBottom={18}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => navigate(paths.visualGuide)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                color: c.text2,
                background: 'none',
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              <BookOpen size={12} />
              Guide
            </button>
          </div>
        }
      />

      {/*
        The stages, in order. `preparing` and `mixing` replace the page rather than
        sitting under it, because in both cases the thing underneath is the thing being
        prepared — leaving a live genre picker behind a "preparing your workspace" notice
        would contradict it. Generation is the opposite: the wave goes below the form,
        which stays legible, the way it does on Create.
      */}
      {stage === 'preparing' ? (
        <ProcessingWave size="lg" seed="Lyrixa-mixer-preparing" {...WAITING.preparing} />
      ) : stage === 'mixing' ? (
        <ProcessingWave
          size="lg"
          // Seeded from the blend, so the bars differ per mix rather than being one
          // fixed silhouette every time. Cosmetic, and honest about it: the heights are
          // a hash of two words, not an analysis of them.
          seed={`Lyrixa-mixer-${genre1}-${genre2}`}
          {...WAITING.mixing}
        />
      ) : (
        <>
          {/* Section 1 — Genre Mixer */}
          <MixerGenrePicker
            genre1={genre1}
            genre2={genre2}
            genre3={genre3}
            compatibility={compatibility}
            onChange={setSetting}
          />

          {stage === 'genres' && (
            <>
              {error && (
                <p style={{ fontSize: 12, color: c.danger, margin: '0 0 10px' }}>{error}</p>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  background: c.surface1,
                  border: `1px solid ${c.border}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 18,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  {/* The product's words, verbatim. It is an instruction rather than an
                      error, so it is here from the start in text2 — not raised in red
                      once the user gets it wrong. */}
                  <p style={{ fontSize: 13, color: c.text1, margin: '0 0 3px' }}>
                    Select at least two genres to create your mix.
                  </p>
                  <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
                    {/* Counts all three slots, the optional one included — see
                        selectGenreCount. `of 2` is the minimum, not a capacity. */}
                    {genreCount} of {MIXER_MIN_GENRES} selected
                  </p>
                </div>
                {/*
                  Disabled until two genres are chosen, which is the requirement stated
                  as a property of the button rather than as a message after the fact.
                  `mix()` guards the same rule again.
                */}
                <Btn onClick={() => void mix()} disabled={!ready}>
                  <Shuffle size={14} />
                  Mix
                </Btn>
              </div>
            </>
          )}
        </>
      )}

      {stage === 'details' && (
        <>
          {/* Section 2 — Creative controls */}
          <MixerControls
            settings={{ mood, tempo, language, vocal, length, energy }}
            onChange={setSetting}
          />

          {/* Section 3 — Music DNA preview */}
          {dna && <MixerDnaPreview dna={dna} />}

          {/* Section 4 — Live prompt generator */}
          <MixerPromptPanel
            prompt={prompt}
            extraDescription={extraDescription}
            onChange={setPrompt}
            onExtraDescriptionChange={setExtraDescription}
            onCopy={() => {
              navigator.clipboard?.writeText(prompt);
              showToast('Prompt copied');
            }}
            onRegenerate={() => {
              regeneratePrompt();
              showToast('Prompt regenerated');
            }}
            onSave={() => {
              // The prototype's guard: an empty prompt is not "saved".
              if (!prompt.trim()) return;
              downloadTextFile('genre_mixer_prompt.txt', `${prompt.trim()}\n`);
              showToast('Prompt downloaded');
            }}
          />

          {/* Section 5 — AI variations */}
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>AI variations</p>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}
            role="radiogroup"
            aria-label="AI variation"
          >
            {GM_VARIATIONS.map((label) => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={variation === label}
                onClick={() => selectVariation(label)}
                style={{
                  flex: '1 1 140px',
                  textAlign: 'left',
                  padding: '10px 14px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border:
                    variation === label ? `2px solid ${c.accentBorder}` : `1px solid ${c.border}`,
                  background: variation === label ? c.accentSoft : c.surface1,
                  color: variation === label ? c.accent : c.text1,
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: 'Inter',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Section 6 — Preview chips */}
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Preview</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
            <PreviewChip>
              Genre Blend: {genre1} + {genre2}
              {genre3 ? ` + ${genre3}` : ''}
            </PreviewChip>
            <PreviewChip>Mood: {mood}</PreviewChip>
            <PreviewChip>Tempo: {dna?.bpm} BPM</PreviewChip>
            <PreviewChip>Danceability: {dna?.danceability}%</PreviewChip>
            <PreviewChip>Commercial Score: {dna?.commercial}%</PreviewChip>
          </div>

          {/* Section 7 — Generate */}
          {/* role="alert", because this line appears in response to pressing Generate and is
              the only report that it did not start — the same reason MusicDNA's refusal and
              the app's load failures are live regions. */}
          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '0 0 10px' }}>
              {error}
            </p>
          )}
          <div
            style={{
              background: c.surface1,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 18,
            }}
          >
            <Btn full onClick={() => void generate()} disabled={generating}>
              <Music2 size={14} />
              {generating ? 'Generating…' : 'Create'}
            </Btn>
          </div>

          {/*
            Section 8 — Generating.

            The same wave the composer and Music DNA show, in place of the four progress
            rows that were here. Those rows named four stages ("Analyzing Genres",
            "Rendering Audio") and ticked between them on a 320ms interval with no
            connection to the request — a progress display reporting elapsed time as work
            done, which is exactly what the product asks not to be built. The wave claims
            nothing but "working", which is true, and this wait is a real one.
          */}
          {generating && (
            <ProcessingWave
              size="lg"
              seed={`Lyrixa-mixer-generate-${genre1}-${genre2}`}
              style={{ marginBottom: 18 }}
              {...WAITING.generating}
            />
          )}

          {/*
            Section 9 — Result.

            NO TRANSPORT PASSED, because there is no longer one to pass — Batch 6 §7 removed
            the playback card from this area, and with it the four playback props this call
            used to thread through (`playing`, `active`, `elapsed`, `speed`, `onTogglePlay`).
            The song itself is untouched: it is still named here, still downloadable, and
            still one press from the Studio. See MixerResultCard's header for the argument.

            Which is also why this screen no longer reads `usePlayer()` at all. The mixer
            hands its result to the composer and navigates there (see `generate` above), so
            the transport for this song is Create's result panel and, from there, the global
            mini player — the app's normal player system rather than a third view of it.
          */}
          {resultSong && !generating && (
            <MixerResultCard
              song={resultSong}
              onDownload={() => downloadSong(resultSong)}
              onOpenStudio={() => navigate(paths.song(resultSong.id))}
            />
          )}
        </>
      )}

      {/* Section 10 — How it works */}
      <p style={{ fontSize: 12, color: c.text2, margin: '22px 0 10px' }}>How it works</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
        {HOW_IT_WORKS.map((step, i) => (
          <span key={step} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                fontSize: 11,
                color: c.text2,
                background: c.surface1,
                border: `1px solid ${c.border}`,
                borderRadius: 999,
                padding: '4px 10px',
              }}
            >
              {step}
            </span>
            {i < HOW_IT_WORKS.length - 1 && <ChevronRight size={12} color={c.text3} />}
          </span>
        ))}
      </div>
    </div>
  );
}
